import BlockMedia from 'components/BlockMedia';
import Icon from 'components/Icon';
import Img from 'components/Img';
import Link from 'components/Link';
import {
	ScArrowLink,
	ScButton,
	type ValidSitecoreLink,
} from 'components/SitecoreLink';
import Text from 'components/Text';
import type { GTMItemListId, GTMItemListName } from 'hooks/gtm';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type { Field, HeadingLevel } from 'models/sitecore';
import type { ColorString } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

interface CategoryIcon {
	iconUrl?: string;
	title: string;
	url: string;
}

interface SupportItem {
	campaignBubbleLabel?: string;
	campaignBubbleSale?: boolean;
	campaignBubbleText?: string;
	id: string;
	imageAlt: string;
	imageSrc?: string;
	link?: ValidSitecoreLink;
	video?: DigizuiteAsset;
}

interface Props {
	basePriceTextBackground?: ColorString;
	campaignBubbleLabel?: string;
	campaignBubbleSale?: boolean;
	campaignBubbleText?: string;
	categoryIcons?: CategoryIcon[];
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	heading?: Field<string>;
	headingLevel?: HeadingLevel;
	preventAutoplay?: boolean;
	primaryLargeImageAlt: string | undefined;
	primaryLargeImageSrc: string | undefined;
	primaryLargeVideo?: DigizuiteAsset;
	primaryLinkHref?: string;
	primaryLinks?: ValidSitecoreLink[];
	primarySmallImageAlt: string | undefined;
	primarySmallImageSrc: string | undefined;
	primarySmallVideo?: DigizuiteAsset;
	products?: ProductCard[];
	supportItems: SupportItem[];
}

/** The two smaller items with up to a single link each. */
function SupportItems({
	className,
	items,
	mediaClassName,
}: {
	className?: string;
	items: SupportItem[];
	mediaClassName?: string;
}) {
	return (
		<>
			{items.map(
				({
					campaignBubbleLabel,
					campaignBubbleSale,
					campaignBubbleText,
					id,
					imageAlt,
					imageSrc,
					link,
					video,
				}) => (
					<div key={id} className={cn('relative', className)}>
						<BlockMedia
							image={{
								src: imageSrc,
								alt: imageAlt,
								variant: 'landscape2x1Small',
							}}
							className="md:h-full"
							mediaClassName={mediaClassName}
							video={video}
							href={link?.linktype === 'popover' ? undefined : link?.href}
							mediaWrapperClassName="md:h-full"
							campaignBubbleSmallSize="small"
							campaignBubbleLabel={campaignBubbleLabel}
							campaignBubbleSale={campaignBubbleSale}
							campaignBubbleText={campaignBubbleText}
						/>
						{link && (
							<>
								<ScButton
									variant="secondary"
									link={link}
									className="absolute bottom-4 left-4 focus-visible:focus-outline-inverted max-md:hidden"
								>
									{link.text}
								</ScButton>
								<ScArrowLink link={link} className="mt-3 md:hidden" />
							</>
						)}
					</div>
				),
			)}
		</>
	);
}
SupportItems.displayName = 'PromoStartPage_SupportItems';

function PrimaryLinks({
	className,
	linkClassName,
	links,
}: {
	className: string;
	linkClassName?: string;
	links: ValidSitecoreLink[] | undefined;
}) {
	if (!is.arrayWithLength(links)) {
		return null;
	}
	return (
		<div className={cn(className, 'flex flex-wrap items-start gap-4')}>
			{links.map((link) => (
				<ScButton
					key={link.href}
					variant="secondary"
					link={link}
					className={linkClassName}
				>
					{link.text}
				</ScButton>
			))}
		</div>
	);
}
PrimaryLinks.displayName = 'PromoStartPage_PrimaryLinks';

export default function PromoStartPage({
	basePriceTextBackground,
	campaignBubbleLabel,
	campaignBubbleSale,
	campaignBubbleText,
	categoryIcons,
	gtmItemListId,
	gtmItemListName,
	heading,
	headingLevel = 'h2',
	preventAutoplay,
	primaryLargeImageAlt,
	primaryLargeImageSrc,
	primaryLargeVideo,
	primaryLinkHref,
	primaryLinks,
	primarySmallImageAlt,
	primarySmallImageSrc,
	primarySmallVideo,
	products,
	supportItems,
}: Props) {
	return (
		<>
			<div className="md:grid md:grid-cols-12 md:gap-6">
				<div className="relative md:col-span-8 md:row-span-2">
					<BlockMedia
						mediaLoading="eager"
						image={[
							{
								src: primarySmallImageSrc,
								alt: primarySmallImageAlt,
								variant: 'landscape4x3Small',
								className: 'sm:hidden',
							},
							{
								src: primaryLargeImageSrc,
								alt: primaryLargeImageAlt,
								variant: 'landscape2x1',
								className: 'max-sm:hidden',
							},
						]}
						video={[
							primaryLargeVideo && {
								...primaryLargeVideo,
								className: 'max-sm:hidden',
							},
							primarySmallVideo && {
								...primarySmallVideo,
								className: 'sm:hidden',
							},
						].filter(is.truthy)}
						href={primaryLinkHref}
						className="h-full overflow-hidden max-sm:-mx-4"
						mediaWrapperClassName="h-full"
						mediaClassName="h-full w-full object-cover"
						campaignBubbleLargeSize="xLarge"
						campaignBubbleLabel={campaignBubbleLabel}
						campaignBubbleSale={campaignBubbleSale}
						campaignBubbleText={campaignBubbleText}
						products={products}
						productsDisplayType="campaignSpinner"
						preventAutoplay={preventAutoplay}
						basePriceTextBackground={basePriceTextBackground}
						productAnimationPlacementClassName={cn(
							'flex justify-center',
							'max-sm:top-16 max-sm:bottom-8 max-sm:left-1/2 max-sm:-translate-x-1/2',
							'sm:top-10 sm:right-4 sm:bottom-10 sm:w-1/2',
							'min-[800px]:top-16 min-[800px]:right-8 min-[800px]:bottom-16',
							'md:top-10 md:right-4 md:bottom-10',
							'lg:top-16 lg:bottom-16',
							'xl:top-20 xl:bottom-20',
						)}
						gtmItemListId={gtmItemListId}
						gtmItemListName={gtmItemListName}
						beforeButtonsContent={
							<PrimaryLinks
								links={primaryLinks}
								className="absolute right-4 bottom-4 left-4 max-md:hidden"
								linkClassName="relative after:absolute after:-inset-x-2 after:-inset-y-4 focus-visible:focus-outline-inverted"
							/>
						}
					/>
					<PrimaryLinks
						links={primaryLinks}
						className="mt-4 max-sm:flex-col md:hidden"
					/>
				</div>
				<SupportItems
					items={supportItems}
					className="col-span-4 max-md:hidden"
					mediaClassName="h-full w-full object-cover"
				/>
			</div>

			{is.arrayWithLength(categoryIcons) && (
				<>
					<Text
						as={headingLevel}
						styleAs="h1"
						field={heading}
						className="mt-8"
					/>
					<ul className="mt-6 grid auto-rows-fr grid-cols-3 gap-y-6 sm:grid-cols-4 md:grid-cols-6 lg:flex lg:justify-center">
						{categoryIcons.map(({ iconUrl, title, url }) => (
							<li key={`${url}-${title}`} className="text-center lg:flex-1">
								<Link
									href={url}
									className="group inline-flex flex-col items-center px-1 text-center"
								>
									{iconUrl ? (
										<Img
											src={iconUrl}
											alt=""
											width={48}
											height={48}
											jpgOptimized={false}
											loading="eager"
											className="size-12"
										/>
									) : (
										<Icon
											name="star"
											size={null}
											className="size-8 md:size-12"
										/>
									)}
									<span className="mt-2 min-w-[6em] font-body text-sm group-hover:underline md:text-md">
										{title}
									</span>
								</Link>
							</li>
						))}
					</ul>
				</>
			)}

			<div className="mt-8 grid gap-6 sm:grid-cols-2 md:hidden">
				<SupportItems items={supportItems} mediaClassName="w-full" />
			</div>
		</>
	);
}
PromoStartPage.displayName = 'PromoStartPage';
